const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp']);

export function getFileExtension(file: File): string {
  const fromName = file.name.split('.').pop()?.toLowerCase();
  if (fromName && IMAGE_EXTENSIONS.has(fromName)) return fromName;
  const fromType = file.type.split('/').pop()?.toLowerCase();
  return fromType && IMAGE_EXTENSIONS.has(fromType) ? fromType : 'png';
}

export function isImageFile(file: File): boolean {
  const fromName = file.name.split('.').pop()?.toLowerCase();
  return file.type.startsWith('image/') || Boolean(fromName && IMAGE_EXTENSIONS.has(fromName));
}

export function toAttachmentFile(file: File, index: number): File {
  const extension = getFileExtension(file);
  const name = `attachment_${Date.now()}_${index + 1}.${extension}`;
  return new File([file], name, { type: file.type || `image/${extension}` });
}

export function getPathName(path: string): string {
  return path.split('/').pop() || path;
}

export type ToolPresentationKind = 'read' | 'write' | 'run' | 'search' | 'list' | 'work';

export function getToolPresentation(toolName: string): {
  kind: ToolPresentationKind;
  label: string;
} {
  const name = toolName.toLowerCase();

  if (name.includes('read') || name === 'read') return { kind: 'read', label: 'Reading' };
  if (name.includes('write') || name.includes('edit') || name === 'write' || name === 'edit') {
    return { kind: 'write', label: 'Writing' };
  }
  if (name.includes('bash') || name.includes('terminal') || name.includes('exec')) {
    return { kind: 'run', label: 'Running' };
  }
  if (name.includes('search') || name.includes('grep') || name.includes('glob') || name.includes('find')) {
    return { kind: 'search', label: 'Searching' };
  }
  if (name.includes('list') || name.includes('ls') || name.includes('dir')) {
    return { kind: 'list', label: 'Listing' };
  }

  return { kind: 'work', label: 'Working on' };
}

export function getToolDescription(toolName: string, input: unknown): string {
  if (!input || typeof input !== 'object') return toolName;

  const inputObj = input as Record<string, unknown>;
  if (inputObj.file_path && typeof inputObj.file_path === 'string') {
    return getPathName(inputObj.file_path);
  }
  if (inputObj.path && typeof inputObj.path === 'string') {
    return getPathName(inputObj.path);
  }
  if (inputObj.command && typeof inputObj.command === 'string') {
    return inputObj.command.length > 40 ? `${inputObj.command.slice(0, 40)}...` : inputObj.command;
  }
  if (inputObj.pattern && typeof inputObj.pattern === 'string') {
    return `"${inputObj.pattern}"`;
  }
  if (inputObj.query && typeof inputObj.query === 'string') {
    return `"${inputObj.query}"`;
  }

  return toolName;
}

export function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
